<template>
  <div id="user-groups-page">
    <h2 class="intro-y text-lg font-medium mt-10">
      組別 (共{{ total }}個)
    </h2>

    <div v-if="checkPermission(permissions, 'site.user-group.view')">
      <div class="grid grid-cols-12 gap-6 mt-5">
        <div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
          <router-link to="/management/create-user-group" v-if="checkPermission(permissions, 'site.user-group.create')" class="btn btn-primary shadow-md mr-2">建立組別</router-link>
          <div class="hidden md:block mx-auto text-slate-500">正在顯示第 {{ from }} 至 {{ to }} 個組別</div>
          <div v-if="!loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
            <div class="w-56 relative text-slate-500">
              <input v-model="search" type="text" class="form-control w-56 box pr-10" placeholder="搜尋" @keyup.enter="loadUserGroups">
              <font-awesome-icon @click="loadUserGroups" icon="fa-solid fa-magnifying-glass" class="absolute my-auto w-4 h-4 mr-3 right-0 inset-y-0" />
            </div>
          </div>
          <div v-show="loading" class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0 flex">
            <span><i data-loading-icon="oval" class="w-8 h-8"></i></span>
          </div>
        </div>
        <!-- BEGIN: Data List -->
        <div class="intro-y col-span-12 overflow-auto lg:overflow-visible">
          <table class="table table-report -mt-2">
            <thead>
            <tr>
              <th class="whitespace-nowrap">名稱</th>
              <th>動作</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, key) in userGroups" :key="key" class="intro-x">
              <td>
                <div class="font-medium whitespace-nowrap">{{ item.display_name }}</div>
                <div class="text-slate-500 text-xs whitespace-nowrap mt-0.5">{{ item.description }}</div>
              </td>
              <td class="table-report__action w-56">
                <div class="flex justify-center items-center">
                  <router-link :to="`/management/user-groups/${item.group_id}/users`" class="flex items-center mr-3 text-primary">
                    <font-awesome-icon icon="fa-solid fa-user" />
                    &nbsp;用戶 </router-link>
                  <router-link :to="`/management/update-user-group/${item.group_id}`" v-if="!item.is_system && checkPermission(permissions, 'site.user-group.edit')" class="flex items-center mr-3">
                    <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                    &nbsp;更改 </router-link>
                  <button class="flex items-center text-danger" v-if="!item.is_system && checkPermission(permissions, 'site.user-group.delete')" @click="openDeleteModal(item.group_id)">
                    <font-awesome-icon icon="fa-regular fa-trash-can" />
                    &nbsp;刪除 </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <!-- END: Data List -->
        <!-- BEGIN: Pagination -->
        <pagination-row :pages="pages" :current-page.sync="currentPage" :item-per-page.sync="itemPerPage"></pagination-row>
        <!-- END: Pagination -->
      </div>
      <!-- BEGIN: Delete Confirmation Modal -->
      <div id="delete-confirmation-modal" class="modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-body p-0">
              <div class="p-5 text-center">
                <font-awesome-icon icon="fa-regular fa-circle-xmark" class="fa-3x text-danger" />
                <div class="text-3xl mt-5">你確定嗎？</div>
                <div class="text-slate-500 mt-2">
                  你是否要刪除此組別？
                  <br>
                  這動作不能還原。
                </div>
              </div>
              <div class="px-5 pb-8 text-center">
                <button type="button" data-tw-dismiss="modal" class="btn btn-outline-secondary w-24 mr-1">取消</button>
                <button type="button" class="btn btn-danger w-24" @click="onDelete">確定</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- END: Delete Confirmation Modal -->
    </div>

    <div v-else-if="mounted">
      <div class="alert alert-danger show mt-2" role="alert">沒有權限查看</div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['auth', 'permissions', 'currentSite'],
  data() {
    return {
      mounted: false,
      loading: 0,

      userGroups: [],

      isEffective: true,
      itemPerPage: 50,
      currentPage: 1,
      total: 0,
      pages: 0,
      from: 0,
      to: 0,
      search: '',

      deleteId: null
    }
  },
  watch: {
    auth(val) {
      if (val) {
        this.init()
      }
    },
    async currentSite(val) {
      this.init()
    },
    currentPage(val) {
      this.loadUserGroups(false)
    },
    itemPerPage(val) {
      this.loadUserGroups()
    },
    search(val) {
      if (val === '') {
        this.loadUserGroups()
      }
    }
  },
  async mounted() {
    window.tailwind.svgLoader()

    if (!this.auth) {
      return
    }

    await this.init()
  },
  methods: {
    async init() {
      if (this.currentSite) {
        await this.loadUserGroups()

        setTimeout(() => {
          this.mounted = true
        }, 500)
      }
    },
    async loadUserGroups(resetCurrentPage = true) {
      this.loading++
      if (resetCurrentPage) {
        this.currentPage = 1
      }

      const response = await this.getUserGroups(this.currentSite.site_id, this.search, this.itemPerPage, this.currentPage)
      this.userGroups = response.data
      this.total = response.meta.page.total
      this.pages = response.meta.page.last_page
      this.from = response.meta.page.from ?? 0
      this.to = response.meta.page.to ?? 0

      this.loading--
    },
    openDeleteModal(id) {
      this.deleteId = id

      const el = document.querySelector('#delete-confirmation-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.show()
    },
    async onDelete() {
      const el = document.querySelector('#delete-confirmation-modal')
      const modal = window.tailwind.Modal.getOrCreateInstance(el)
      modal.hide()

      const response = await this.deleteUserGroup(this.currentSite.site_id, this.deleteId)

      let option = {
        title: '成功',
        message: '組別已被刪除',
        type: 'success'
      }

      if (response.code === 0) {
        await this.loadUserGroups()
      } else {
        option = {
          title: '未能刪除組別',
          message: response.data.message ?? '系統錯誤，請稍後再試',
          type: 'error'
        }
      }

      this.$notify(option)
    }
  }
}
</script>

<style lang="scss" scoped>
#user-group-page {
  .table-report__action {
    width: 18rem;
  }
}
</style>
